import { Link } from "react-router-dom";
import { Phone, ArrowRight, MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const serviceAreas = [
  { 
    city: "Joliet", 
    state: "IL",
    description: "Our home base. Full landscaping, paver, concrete, and snow removal services.",
    primary: true
  },
  { 
    city: "New Lenox", 
    state: "IL",
    description: "Paver patio contractor and lawn care services for New Lenox residents.",
    primary: true
  },
  { 
    city: "Frankfort", 
    state: "IL",
    description: "Retaining walls, landscape design, and maintenance in Frankfort.",
    primary: true
  },
  { 
    city: "Manhattan", 
    state: "IL",
    description: "Complete landscaping solutions for Manhattan homeowners.",
    primary: false
  },
  { 
    city: "Elwood", 
    state: "IL",
    description: "Residential and commercial landscaping in Elwood.",
    primary: false
  },
  { 
    city: "Orland Park", 
    state: "IL",
    description: "Premium paver patios and walkways for Orland Park properties.",
    primary: true
  },
  { 
    city: "Homer Glen", 
    state: "IL",
    description: "Custom landscape design and installation in Homer Glen.",
    primary: false
  },
  { 
    city: "Shorewood", 
    state: "IL",
    description: "Lawn maintenance and snow removal services in Shorewood.",
    primary: false
  },
  { 
    city: "Morris", 
    state: "IL",
    description: "Landscaping and concrete services for Morris area.",
    primary: false
  },
  { 
    city: "Naperville", 
    state: "IL",
    description: "Professional concrete driveways and patios in Naperville.",
    primary: true
  },
  { 
    city: "Aurora", 
    state: "IL",
    description: "Full-service landscaping for Aurora homes and businesses.",
    primary: true
  },
  { 
    city: "Schaumburg", 
    state: "IL",
    description: "Commercial landscaping and snow management in Schaumburg.",
    primary: false
  }
];

const keywords = [
  "Landscaping company near Joliet",
  "Paver patio contractor in Naperville",
  "Snow removal services in Will County",
  "Retaining wall builder near me",
  "Concrete contractor Joliet IL",
  "Lawn care services New Lenox",
  "Commercial landscaping Aurora",
  "Residential snow plowing Frankfort"
];

export default function ServiceAreasPage() {
  return (
    <div data-testid="service-areas-page">
      {/* Page Header */}
      <section className="bg-[#F9FAFB] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#2F5930] font-semibold mb-3 tracking-wide uppercase text-sm">
              Service Areas
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-[#1F2937] mb-6 font-['Manrope']">
              Landscaping Services Throughout Will County & Beyond
            </h1>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              J Guzman Landscaping LLC proudly serves Joliet, IL and surrounding communities 
              with professional landscaping, paver installation, retaining walls, concrete work, 
              and snow removal services.
            </p>
          </div>
        </div>
      </section>

      {/* Service Areas Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1F2937] mb-4 font-['Manrope']">
              Cities We Serve
            </h2>
            <p className="text-lg text-[#4B5563] max-w-2xl mx-auto">
              Find professional landscaping services in your area. We travel throughout 
              Will County and neighboring regions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="service-areas-grid">
            {serviceAreas.map((area) => (
              <div 
                key={area.city}
                className={`p-6 rounded-2xl border transition-all duration-300 hover:shadow-lg ${
                  area.primary 
                    ? "bg-white border-[#2F5930]/20 hover:border-[#2F5930]" 
                    : "bg-[#F9FAFB] border-gray-100 hover:border-gray-200"
                }`}
                data-testid={`area-card-${area.city.toLowerCase().replace(" ", "-")}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <MapPin className={`w-5 h-5 mt-1 flex-shrink-0 ${
                    area.primary ? "text-[#2F5930]" : "text-gray-400"
                  }`} />
                  <div>
                    <h3 className="text-xl font-semibold text-[#1F2937] font-['Manrope']">
                      {area.city}, {area.state}
                    </h3>
                    {area.primary && (
                      <span className="inline-block text-xs text-[#2F5930] font-medium bg-[#F0FDF4] px-2 py-0.5 rounded mt-1">
                        Primary Service Area
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-[#4B5563] text-sm leading-relaxed">
                  {area.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 p-8 bg-[#F0FDF4] rounded-2xl border border-[#2F5930]/10">
            <div className="flex items-start gap-4">
              <CheckCircle2 className="w-6 h-6 text-[#2F5930] flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-[#1F2937] mb-2 font-['Manrope']">
                  And Surrounding Areas
                </h3>
                <p className="text-[#4B5563]">
                  Don&apos;t see your city listed? We serve many additional communities throughout 
                  Will County, DuPage County, and Cook County. Contact us to confirm service 
                  availability in your area.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Local SEO Keywords Section */}
      <section className="py-16 md:py-20 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1F2937] mb-4 font-['Manrope']">
              Looking for Local Landscaping Services?
            </h2>
            <p className="text-[#4B5563] max-w-2xl mx-auto">
              Whether you need a landscaping company near you or a specific service, 
              J Guzman Landscaping is here to help.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {keywords.map((keyword, index) => (
              <span 
                key={index}
                className="bg-white text-[#4B5563] px-4 py-2 rounded-full text-sm border border-gray-200 hover:border-[#2F5930] hover:text-[#2F5930] transition-colors cursor-default"
              >
                {keyword}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Services in Your Area */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1F2937] mb-6 font-['Manrope']">
                Full-Service Landscaping Across Illinois
              </h2>
              <p className="text-lg text-[#4B5563] mb-8 leading-relaxed">
                No matter where you are in our service area, you&apos;ll receive the same 
                high-quality workmanship and professional service. Our team travels 
                throughout the region to serve both residential and commercial clients.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#2F5930]" />
                  <span className="text-[#4B5563]">Landscape installation in Joliet, New Lenox, Frankfort</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#2F5930]" />
                  <span className="text-[#4B5563]">Paver patios in Naperville, Aurora, Orland Park</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#2F5930]" />
                  <span className="text-[#4B5563]">Snow removal throughout Will County, Illinois</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#2F5930]" />
                  <span className="text-[#4B5563]">Concrete services in Homer Glen, Shorewood, Morris</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button 
                    className="bg-[#2F5930] hover:bg-[#244525] text-white rounded-full px-6"
                    data-testid="areas-contact-btn"
                  >
                    Contact Us
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <a href="tel:8159098790">
                  <Button 
                    variant="outline"
                    className="text-[#2F5930] border-[#2F5930] hover:bg-[#F0FDF4] rounded-full px-6"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    (815) 909-8790
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/tpx083x2_Screenshot%202026-02-25%20at%203.46.39%E2%80%AFPM.png"
                alt="Landscaping services by J Guzman in Will County Illinois"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#2F5930] py-16 md:py-20" data-testid="areas-cta">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-['Manrope']">
            Serving Your Community
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            From Joliet to Naperville, we bring professional landscaping services to your doorstep. 
            Call today for a free estimate.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:8159098790">
              <Button 
                size="lg"
                className="bg-white text-[#2F5930] hover:bg-gray-100 rounded-full px-8"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call (815) 909-8790
              </Button>
            </a>
            <Link to="/contact">
              <Button 
                size="lg"
                variant="outline"
                className="bg-transparent text-white border-2 border-white hover:bg-white/10 rounded-full px-8"
              >
                Get Your Free Estimate
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
